import React from 'react';
import { Linking, Text, View, Dimensions } from 'react-native';
import i18n from 'i18n-js';

import { Element, scale as initialScale, fullWidth, notchBottom, width, height, notchTop } from '@components/frame';
import { font as initialFont } from '@styles/fonts';
import Svg from '@styles/icons/svg';
import TouchableElement from '@components/common/touchableElement';
import { isPhone, isWeb, isIOS, compareOrder, Alert, toBase64, ImageUpload, localTime, Popup, navigationRef, parseRouteParamAsNumber } from '@helpers';
import BackButton from '@components/common/backButton';
import SelectFavorite from './components/modal/selectFavorite';
import { DETAILS_SCREEN_TAB_NAME, modalBg } from '@v2/pages/common/constants/base.constants';
import { convertAngle } from '@v2/components/imageHandling/helpers';

export const favoriteKey = ['Part', 'Clamping'];

export const TAG_KEYS = {
  1: 'Part',
  2: 'ProductionOrder',
  3: 'Operation',
  4: 'Clamping',
  5: 'Recipe',
  6: 'Employee',
  7: 'UserAccount',
  8: 'Machine',
  9: 'RecipeOperation',
  10: 'ProductionBatch',
  11: 'Tool',
};

export const KEYS_TAGS = {
  Part: 1,
  ProductionOrder: 2,
  Operation: 3,
  ProductionOperation: 3,
  Clamping: 4,
  NcFile: 4,
  Recipe: 5,
  Employee: 6,
  UserAccount: 7,
  Machine: 8,
  RecipeOperation: 9,
  Batch: 10,
  ProductionBatch: 10,
  Tool: 11,
};

export const INDIRECT_MACHINE_LINK_SCOPE_IDS = [KEYS_TAGS.Clamping, KEYS_TAGS.Part];

export const DETAILS_SCREEN_TAB_NAME_TO_SCOPE_ID = {
  [DETAILS_SCREEN_TAB_NAME.Part]: KEYS_TAGS.Part,
  [DETAILS_SCREEN_TAB_NAME.Jobs]: KEYS_TAGS.Operation,
  [DETAILS_SCREEN_TAB_NAME.Order]: KEYS_TAGS.ProductionOrder,
  [DETAILS_SCREEN_TAB_NAME.Batch]: KEYS_TAGS.ProductionBatch,
  [DETAILS_SCREEN_TAB_NAME.NCFile]: KEYS_TAGS.Clamping,
  [DETAILS_SCREEN_TAB_NAME.Tools]: KEYS_TAGS.Tool,
};

export const DOCUMENT_ENTITY_TYPE = {
  Part: 'Part',
  Recipe: 'Recipe',
  RecipeOperation: 'RecipeOperation',
  NcFile: 'NcFile',
  ProductionOrder: 'ProductionOrder',
  ProductionOperation: 'ProductionOperation',
  Batch: 'Batch',
  Tool: 'Tool',
};

export const DOCUMENT_TYPE_TAG_KEY_MAP = {
  [DOCUMENT_ENTITY_TYPE.Part]: KEYS_TAGS.Part,
  [DOCUMENT_ENTITY_TYPE.Recipe]: KEYS_TAGS.Recipe,
  [DOCUMENT_ENTITY_TYPE.RecipeOperation]: KEYS_TAGS.RecipeOperation,
  [DOCUMENT_ENTITY_TYPE.NcFile]: KEYS_TAGS.Clamping,
  [DOCUMENT_ENTITY_TYPE.Batch]: KEYS_TAGS.ProductionBatch,
  [DOCUMENT_ENTITY_TYPE.ProductionOrder]: KEYS_TAGS.ProductionOrder,
  [DOCUMENT_ENTITY_TYPE.ProductionOperation]: KEYS_TAGS.Operation,
  [DOCUMENT_ENTITY_TYPE.Tool]: KEYS_TAGS.Tool,
};

export const TAG_NAMING = {
  [DOCUMENT_ENTITY_TYPE.ProductionOrder]: 'Production Order',
  Operation: 'Operation',
  [DOCUMENT_ENTITY_TYPE.ProductionOperation]: 'Production Operation',
  Clamping: 'NC File',
  [DOCUMENT_ENTITY_TYPE.NcFile]: 'NC File',
  [DOCUMENT_ENTITY_TYPE.Recipe]: 'Recipe',
  [DOCUMENT_ENTITY_TYPE.RecipeOperation]: 'Recipe Operation',
  ProductionBatch: 'Batch',
  [DOCUMENT_ENTITY_TYPE.Batch]: 'Batch',
  Employee: 'Employee',
  Machine: 'Machine',
  [DOCUMENT_ENTITY_TYPE.Part]: 'Part',
  UserAccount: 'User',
  [DOCUMENT_ENTITY_TYPE.Tool]: 'Tool',
};

export const OP_TAG_NAMING = {
  [DOCUMENT_ENTITY_TYPE.ProductionOrder]: 'Production Order',
  Operation: 'Job',
  [DOCUMENT_ENTITY_TYPE.ProductionOperation]: 'Job',
  Clamping: 'NC File',
  [DOCUMENT_ENTITY_TYPE.NcFile]: 'NC File',
  [DOCUMENT_ENTITY_TYPE.Recipe]: 'Recipe',
  [DOCUMENT_ENTITY_TYPE.RecipeOperation]: 'Recipe Operation',
  ProductionBatch: 'Batch',
  [DOCUMENT_ENTITY_TYPE.Batch]: 'Batch',
  Employee: 'Employee',
  Machine: 'Machine',
  [DOCUMENT_ENTITY_TYPE.Part]: 'Part',
  UserAccount: 'User',
  [DOCUMENT_ENTITY_TYPE.Tool]: 'Tool',
};

export const SCOPE_ID_TO_TAG_NAME = {
  [KEYS_TAGS.Part]: TAG_NAMING.Part,
  [KEYS_TAGS.Recipe]: TAG_NAMING.Recipe,
  [KEYS_TAGS.RecipeOperation]: TAG_NAMING.RecipeOperation,
  [KEYS_TAGS.Clamping]: TAG_NAMING.NcFile,
  [KEYS_TAGS.ProductionBatch]: TAG_NAMING.Batch,
  [KEYS_TAGS.ProductionOrder]: TAG_NAMING.ProductionOrder,
  [KEYS_TAGS.Operation]: TAG_NAMING.ProductionOperation,
  [KEYS_TAGS.Tool]: TAG_NAMING.Tool,
  [KEYS_TAGS.Machine]: TAG_NAMING.Machine,
  [KEYS_TAGS.Employee]: TAG_NAMING.Employee,
  [KEYS_TAGS.UserAccount]: TAG_NAMING.UserAccount,
};

export const IMAGE_LABEL = {
  [DOCUMENT_ENTITY_TYPE.ProductionOrder]: 'Production Order',
  Operation: 'Job',
  [DOCUMENT_ENTITY_TYPE.ProductionOperation]: 'Job',
  Clamping: 'Clamping',
  [DOCUMENT_ENTITY_TYPE.NcFile]: 'Clamping',
  [DOCUMENT_ENTITY_TYPE.Recipe]: 'Recipe',
  [DOCUMENT_ENTITY_TYPE.RecipeOperation]: 'Recipe Operation',
  ProductionBatch: 'Batch',
  [DOCUMENT_ENTITY_TYPE.Batch]: 'Batch',
  Employee: 'Employee',
  Machine: 'Machine',
  [DOCUMENT_ENTITY_TYPE.Part]: 'Part',
  UserAccount: 'User',
  [DOCUMENT_ENTITY_TYPE.Tool]: 'Tool',
};

export const IMAGE_HANDLING_CONTEXT = {
  [DOCUMENT_ENTITY_TYPE.Part]: 'p',
  [DOCUMENT_ENTITY_TYPE.Recipe]: 'r',
  [DOCUMENT_ENTITY_TYPE.RecipeOperation]: 'rOp',
  [DOCUMENT_ENTITY_TYPE.NcFile]: 'c',
  [DOCUMENT_ENTITY_TYPE.Batch]: 'b',
  [DOCUMENT_ENTITY_TYPE.ProductionOrder]: 'pO',
  [DOCUMENT_ENTITY_TYPE.ProductionOperation]: 'pOp',
  [DOCUMENT_ENTITY_TYPE.Tool]: 't',
  Machine: 'm',
  User: 'u',
};

export const CONTEXT_TO_NAME = {
  p: DOCUMENT_ENTITY_TYPE.Part,
  r: DOCUMENT_ENTITY_TYPE.Recipe,
  rOp: DOCUMENT_ENTITY_TYPE.RecipeOperation,
  c: DOCUMENT_ENTITY_TYPE.NcFile,
  b: DOCUMENT_ENTITY_TYPE.Batch,
  pO: DOCUMENT_ENTITY_TYPE.ProductionOrder,
  pOp: DOCUMENT_ENTITY_TYPE.ProductionOperation,
  t: DOCUMENT_ENTITY_TYPE.Tool,
};

export const DOCUMENT_RELATED_IDS_KEYS = {
  [DOCUMENT_ENTITY_TYPE.Part]: 'relatedPartIds',
  [DOCUMENT_ENTITY_TYPE.Recipe]: 'relatedRecipeIds',
  [DOCUMENT_ENTITY_TYPE.RecipeOperation]: 'relatedRecipeOperationIds',
  [DOCUMENT_ENTITY_TYPE.NcFile]: 'relatedNcFileIds',
  [DOCUMENT_ENTITY_TYPE.Batch]: 'relatedProductionBatchIds',
  [DOCUMENT_ENTITY_TYPE.ProductionOrder]: 'relatedProductionOrderIds',
};

export const DOCUMENT_HIERARCHY_CONFIG = {
  [DOCUMENT_ENTITY_TYPE.Part]: {
    priority: 1,
    requestType: 'filterPartDocumentsRequest',
    responseType: 'filterPartDocumentsResponse',
    scopeKey: 'partIds',
    scopeName: 'Part',
    scopeId: DOCUMENT_TYPE_TAG_KEY_MAP[DOCUMENT_ENTITY_TYPE.Part],
    key: DOCUMENT_ENTITY_TYPE.Part,
    responseKey: DOCUMENT_RELATED_IDS_KEYS.Part,
  },
  [DOCUMENT_ENTITY_TYPE.Recipe]: {
    priority: 2,
    requestType: 'filterRecipeDocumentsRequest',
    responseType: 'filterRecipeDocumentsResponse',
    scopeKey: 'recipeIds',
    scopeName: 'Recipe',
    scopeId: DOCUMENT_TYPE_TAG_KEY_MAP[DOCUMENT_ENTITY_TYPE.Recipe],
    key: DOCUMENT_ENTITY_TYPE.Recipe,
    responseKey: DOCUMENT_RELATED_IDS_KEYS.Recipe,
  },
  [DOCUMENT_ENTITY_TYPE.RecipeOperation]: {
    priority: 3,
    requestType: 'filterRecipeOperationDocumentsRequest',
    responseType: 'filterRecipeOperationDocumentsResponse',
    scopeKey: 'recipeOperationIds',
    scopeName: 'Recipe Operation',
    scopeId: DOCUMENT_TYPE_TAG_KEY_MAP[DOCUMENT_ENTITY_TYPE.RecipeOperation],
    key: DOCUMENT_ENTITY_TYPE.RecipeOperation,
    responseKey: DOCUMENT_RELATED_IDS_KEYS.RecipeOperation,
  },
  [DOCUMENT_ENTITY_TYPE.NcFile]: {
    priority: 4,
    requestType: 'filterNcFileDocumentsRequest',
    responseType: 'filterNcFileDocumentsResponse',
    scopeKey: 'ncFileIds',
    scopeName: 'NC File',
    scopeId: DOCUMENT_TYPE_TAG_KEY_MAP[DOCUMENT_ENTITY_TYPE.NcFile],
    key: DOCUMENT_ENTITY_TYPE.NcFile,
    responseKey: DOCUMENT_RELATED_IDS_KEYS.NcFile,
  },
  [DOCUMENT_ENTITY_TYPE.Batch]: {
    priority: 5,
    requestType: 'filterProductionBatchDocumentsRequest',
    responseType: 'filterProductionBatchDocumentsResponse',
    scopeKey: 'productionBatchIds',
    scopeName: 'Batch',
    scopeId: DOCUMENT_TYPE_TAG_KEY_MAP[DOCUMENT_ENTITY_TYPE.Batch],
    exceptionLinkToConfigItem: DOCUMENT_ENTITY_TYPE.ProductionOperation,
    key: DOCUMENT_ENTITY_TYPE.Batch,
    responseKey: DOCUMENT_RELATED_IDS_KEYS.Batch,
  },
  [DOCUMENT_ENTITY_TYPE.ProductionOrder]: {
    priority: 6,
    requestType: 'filterProductionOrderDocumentsRequest',
    responseType: 'filterProductionOrderDocumentsResponse',
    scopeKey: 'productionOrderIds',
    scopeName: 'Production Order',
    scopeId: DOCUMENT_TYPE_TAG_KEY_MAP[DOCUMENT_ENTITY_TYPE.ProductionOrder],
    key: DOCUMENT_ENTITY_TYPE.ProductionOrder,
    responseKey: DOCUMENT_RELATED_IDS_KEYS.ProductionOrder,
  },
  [DOCUMENT_ENTITY_TYPE.ProductionOperation]: {
    priority: 7,
    requestType: 'filterProductionOperationDocumentsRequest',
    responseType: 'filterProductionOperationDocumentsResponse',
    scopeKey: 'productionOperationIds',
    scopeName: 'Production Operation',
    scopeId: DOCUMENT_TYPE_TAG_KEY_MAP[DOCUMENT_ENTITY_TYPE.ProductionOperation],
    key: DOCUMENT_ENTITY_TYPE.ProductionOperation,
  },
  [DOCUMENT_ENTITY_TYPE.Tool]: {
    priority: 8,
    requestType: 'filterToolDocumentsRequest',
    responseType: 'filterToolDocumentsResponse',
    scopeKey: 'toolIds',
    scopeName: 'Tool',
    scopeId: DOCUMENT_TYPE_TAG_KEY_MAP[DOCUMENT_ENTITY_TYPE.Tool],
    key: DOCUMENT_ENTITY_TYPE.Tool,
  },
};

export const DOCUMENTS_STORE_MODE = {
  DIRECT_INDIRECT_LINKING: 'direct-indirect',
  SCOPE: 'scope',
};

export const INDIRECT_LINKING_TEXT = 'Indirectly Linked Documents';

export const DOC_TYPE = {
  '2D_DRAWING': '2D Drawing',
  '3D_DRAWING': '3D Drawing',
  GENERAL_DOCUMENT: 'General Document',
  IMAGE: 'Image',
  PART_IMAGE: 'Part Image',
  CLAMPING_IMAGE: 'Clamping Image',
  INSPECTION_RESULT: 'Inspection Result',
  MAINTENANCE_PROCEDURE: 'Maintenance Procedure',
  MATERIAL_CERTIFICATE: 'Material Certificate',
  MEASUREMENT_REPORT: 'Measurement Report',
  PURCHASE_ORDER: 'Purchase Order',
  PRODUCTION_ORDER: 'Production Order',
  SETUP_SHEET: 'Setup Sheet',
  TEST_SPECIFICATION: 'Test Specification',
  NOTE: 'Note',
};

export const formatDate = (date) => {
  const today = localTime();
  const yesterday = localTime().subtract(1, 'day');

  const d = localTime(date);

  if (d.isSame(today, 'day')) {
    return `${i18n.t('Today')} ${d.format('HH:mm')}`;
  }

  if (d.isSame(yesterday, 'day')) {
    return `${i18n.t('Yesterday')} ${d.format('HH:mm')}`;
  }

  return `${d.format('DD')} ${i18n.t(d.format('MMMM'))} ${d.format('YYYY HH:mm')}`;
};

const minimalActiveImage = isPhone ? 58 : 54;
export const smallImageHeight = isPhone ? 40 : 60;
export const limitMinimumSize = (w, scale = initialScale) => {
  const maxW = scale(smallImageHeight) * 2;
  if (w > maxW) {
    return maxW;
  }
  if (w < scale(minimalActiveImage)) {
    return scale(minimalActiveImage);
  }
  return w ? w : scale(minimalActiveImage);
};

export const generateImages = async (imageHandling, generateImageURL, scale = initialScale) => {
  const { images = [] } = imageHandling;
  const data = [];

  for (const index in images) {
    const item = images[index];

    if (!item) {
      return;
    }

    let documentTypeValue = item.documentType;

    if ((!documentTypeValue || documentTypeValue === 'Image') && Array.isArray(item.tags) && item.tags.length && typeof item.tags[0] === 'string') {
      documentTypeValue = item.tags[0];
    }

    const linkTagsRaw = Array.isArray(item.links) && item.links.length ? item.links : item.tags;
    const linkTagsSafe = Array.isArray(linkTagsRaw) ? linkTagsRaw.filter((t) => typeof t === 'object') : [];

    item.imageHash = item.imageHash || item.contentHash;

    const details = item.imageDetails || item.details || item.pageDetails;

    if (details?.[0]) {
      const d = details[0];
      item.width = d.width;
      item.height = d.height;
    }

    const isPDF = item.extensionOriginal === 'pdf';

    const activeImageWidth = limitMinimumSize((scale(smallImageHeight) * item.width) / item.height, scale);

    const imageBase = {
      contentHash: item.contentHash,
      id: item.documentId,
      documentId: item.documentId,
      isPDF,
      pageCount: item.pageCount,
      widthOriginal: item.width,
      heightOriginal: item.height,
      page: item.pageNumber,
      fileName: item.fileName,
      extensionOriginal: item.extensionOriginal,
      extensionDerivative: item.extensionDerivative,
      isDocument: item.documentType === 'Other',
      documentType: documentTypeValue,
      documentTag: typeof item?.tags?.[0] !== 'object' ? item?.tags?.[0] : item?.tags?.[0]?.name,
      updatedDate: formatDate(item.updatedDate),
      updatedAt: item.updatedDate ?? item.modifiedOn,
      createdAt: item.createdDate ?? item.createdOn,
      createdBy: item.createdBy,
      modifiedBy: item.modifiedBy,
      fileSize: item.fileSize,
      originalLocation: item.originalLocation,
      sourceName: item.sourceName,
      activeImageWidth,
      links: Array.isArray(item.links) ? item.links : [],
      tags: linkTagsSafe.sort(compareOrder).map((el) => ({
        ...el,
        key: TAG_KEYS[el.scopeId],
        value: el.scopeReferenceId,
        name: el.scopeReferenceName,
        id: Math.random().toString(36).substring(7),
        selected: el.isActivated,
        canBeFavorite: favoriteKey.includes(TAG_KEYS[el.scopeId]),
        favorite: el.isFavorite,
      })),
      cropSettings: item.imageCropSettings || item.cropSettings,
      details,
      downloadUrl: await generateImageURL({ item, download: true, item }),
      title: item.title,
      subTitle: item.subTitle,
    };

    imageBase.cropped = Boolean(imageBase.cropSettings?.find((c) => c.pageNumber == imageBase.page));

    const baseDetails = imageBase.details?.find((c) => c.pageNumber == imageBase.page) || {};

    const customRotate = { [imageBase.page]: imageBase.cropped ? 0 : baseDetails.rotationAngle || 0 };

    if (isPDF) {
      const pagesList = {};
      const lists = Array.from({ length: item.pageCount }, (_, i) => i + 1);
      for (const index in lists) {
        const i = lists[index];
        const b = {
          isPDF,
          page: i,
          item,
        };

        if (imageBase.details) {
          imageBase.details.forEach((i) => {
            const crop = imageBase.cropSettings?.find((c) => c.pageNumber == i.pageNumber) || {};
            if (crop.rotationAngle !== undefined) {
              return;
            }
            customRotate[i.pageNumber] = i.rotationAngle;
          });
        }

        const cropped = Boolean(imageBase.cropSettings?.find((c) => c.pageNumber == i));

        pagesList[i] = {
          url: await generateImageURL({ size: '_3x', cropped, ...b }),
          urlZoom: await generateImageURL({ size: '_large', cropped, ...b }),
          urlSmall: await generateImageURL({ size: '_1x', cropped, ...b }),
          originUrl3x: await generateImageURL({ size: '_3x', cropped: false, ...b }),
          originUrl: await generateImageURL({ size: '_large', cropped: false, ...b }),
        };
      }

      if (customRotate[imageBase.page] == 90 || customRotate[imageBase.page] == 270) {
        imageBase.widthOriginal = item.height;
        imageBase.heightOriginal = item.width;
      }

      data.push({
        ...imageBase,
        customRotate,
        pagesList,
        ...pagesList[imageBase.page],
      });
    } else {
      if (!imageBase.cropped && (customRotate[imageBase.page] == 90 || customRotate[imageBase.page] == 270)) {
        imageBase.widthOriginal = item.height;
        imageBase.heightOriginal = item.width;
      }
      data.push({
        url: await generateImageURL({ size: '_1x', cropped: imageBase.cropped, item }),
        urlZoom: await generateImageURL({ size: '_large', cropped: imageBase.cropped, item }),
        urlSmall: await generateImageURL({ size: '_1x', cropped: imageBase.cropped, item }),
        originUrl: await generateImageURL({ size: '_large', cropped: false, item }),
        originUrl3x: await generateImageURL({ size: '_3x', cropped: false, item }),
        customRotate,
        ...imageBase,
      });
    }
  }
  return data;
};

export const saveImageFN = async (data, { images, selectedIndex, setImages, cropImage, trigerCrop, scale = initialScale }) => {
  const { x, y, height, width, angle, uri } = data;
  const newImage = uri;
  const newArray = images.map((i) => Object.assign({}, i));
  const selected = Object.assign({}, newArray[selectedIndex]);

  const img = images[selectedIndex];
  selected.width = width;
  selected.height = height;
  selected.x = x;
  selected.y = y;
  if (!selected.base64Original) {
    selected.base64Original = selected.base64;
  }
  selected.base64 = newImage;
  selected.urlSmall = newImage;
  selected.angle = angle;
  selected.show = true;
  selected.cropped = true;
  selected.customRotate = { ...selected.customRotate, [selected.page]: 0 };
  selected.activeImageWidth = limitMinimumSize((scale(smallImageHeight) * width) / height, scale);
  newArray[selectedIndex] = selected;
  setImages(newArray);

  let backendData = {};
  const toTwo = (n) => Number(Number(n).toFixed(2));

  if (angle == 0 || angle == 180) {
    backendData = {
      imageCropSettings: {
        upperLeftXPosition: toTwo(x / img.size3x.width),
        lowerRightXPosition: toTwo((x + width) / img.size3x.width),
        upperLeftYPosition: toTwo(y / img.size3x.height),
        lowerRightYPosition: toTwo((y + height) / img.size3x.height),
      },
    };
  } else {
    backendData = {
      imageCropSettings: {
        upperLeftXPosition: toTwo(x / img.size3x.height),
        lowerRightXPosition: toTwo((x + width) / img.size3x.height),
        upperLeftYPosition: toTwo(y / img.size3x.width),
        lowerRightYPosition: toTwo((y + height) / img.size3x.width),
      },
    };
  }
  backendData.documentId = newArray[selectedIndex].id;
  backendData.imageCropSettings.pageNumber = img.page;
  backendData.imageCropSettings.rotationAngle = angle;

  trigerCrop();

  await cropImage(backendData);
};

export const uploadImageFN = async (data, { images, addImage, setImages, list, C, context, toEnd, scale = initialScale }) => {
  if (data && data.errorCode) {
    Alert({
      title: 'No access to camera.',
      text: 'Enable your camera to continue.',
      actions: [
        {
          text: 'Cancel',
          onPress: () => console.log('Cancel Pressed'),
          style: 'primary',
        },
        {
          text: 'Open settings',
          onPress: () => {
            if (isIOS) {
              Linking.openURL('app-settings:');
            } else {
              Linking.openSettings();
            }
          },
        },
      ],
    });
    return;
  }

  if (isWeb) {
    data.uri = await toBase64(data);
  }

  if (data && data.uri) {
    const possibleKeys = ['Part', 'ProductionOrder', 'Operation', 'Clamping', 'Machine'];

    const tags = [];

    Object.keys(context.filters).forEach((key) => {
      if (possibleKeys.includes(key)) {
        tags.push({
          scopeId: KEYS_TAGS[key],
          scopeReferenceId: context.filters[key].value,
          isFavorite: false,
        });
      }
    });

    let sizeInMB;
    if (isWeb) {
      sizeInMB = Number((data.size / (1000 * 1000)).toFixed(1));
    } else {
      if (data.fileSize) {
        sizeInMB = Number((data.fileSize / (1024 * 1024)).toFixed(1));
      } else {
        sizeInMB = Number((data.size / (1000 * 1000)).toFixed(1));
      }
    }

    if (sizeInMB >= 15) {
      Alert({ title: 'The upload failed because your file was too big', text: `The maximum size is 15MB Your file size:${sizeInMB}` });
      return;
    }
    // else {
    //   Alert(`Size:${sizeInMB}`);
    // }
    var photo = isWeb
      ? data
      : {
          uri: data.uri,
          type: data.type,
          name: data.fileName || data.name,
        };

    const tempID = `TEMPIMAGEID-${Math.random().toString(36).substring(7)}`;

    const isPDF = photo.type.includes('pdf');

    addImage({ photo, tags, tempID, isPDF });

    if (isPDF) {
      data.uri = null;
    }

    const activeImageWidth = limitMinimumSize((scale(smallImageHeight) * data.width) / data.height, scale);

    const newImage = {
      id: tempID,
      base64: data.uri,
      url: data.uri,
      urlSmall: data.uri,
      originUrl: data.uri,
      width: data.width,
      height: data.height,
      widthOriginal: data.width,
      heightOriginal: data.height,
      isPreview: isPDF,
      showPDFUploading: isPDF,
      pageCount: 1,
      page: 1,
      fileName: data.fileName,
      local: true,
      customRotate: { 1: 0 },
      activeImageWidth,
      updatedDate: formatDate(localTime().format('YYYY-MM-DD HH:mm')),
      tags: tags.map((el) => ({
        ...el,
        key: TAG_KEYS[el.scopeId],
        value: el.scopeReferenceId,
        name: el.scopeReferenceName,
        id: Math.random().toString(36).substring(7),
        selected: el.selected !== undefined ? el.selected : true,
        canBeFavorite: favoriteKey.includes(TAG_KEYS[el.scopeId]),
        favorite: el.isFavorite,
      })),
    };
    if (toEnd) {
      setImages([...images, newImage]);
    } else {
      setImages([newImage, ...images]);
    }

    setTimeout(() => {
      if (list && list.current) {
        if (toEnd) {
          list.current.scrollToEnd();
        } else {
          list.current.scrollToIndex({ animated: false, index: 0 });
        }
      }
    }, 1000);
  }
};

export const Notification = ({ notification = [], C, scale = initialScale, font = initialFont }) => {
  let isUpload = false;
  const list = [];
  notification.forEach((i) => {
    if (!i.type === 'upload') {
      return list.push(i);
    }
    if (i.type === 'upload' && !isUpload) {
      isUpload = true;
      return list.push(i);
    }
  });

  return (
    <Element
      key="upload image message"
      scale={scale}
      {...{
        config: {
          position: {
            top: isPhone ? 90 : 60,
            right: isPhone ? 14 : 40,
          },
          height: 60,
          zIndex: 10000,
        },
      }}
    >
      {list.map(({ Message = <View />, Additional = <View />, transparent = false, id }, index) => (
        <View
          key={id}
          style={[
            {
              justifyContent: 'space-between',
              flexDirection: 'row',
              alignContent: 'center',
              alignItems: 'center',
              width: 350,
              height: '100%',
              borderRadius: scale(13),
              marginBottom: scale(10),
              paddingRight: scale(10),
              paddingLeft: scale(10),
            },
            !transparent && {
              shadowColor: '#000',
              shadowOffset: {
                width: 0,
                height: 2,
              },
              shadowOpacity: 0.25,
              shadowRadius: 3.84,
              elevation: 5,
              backgroundColor: C(modalBg),
            },
          ]}
        >
          <View style={{ width: scale(250) }}>{Message && <Message {...{ C, font }} />}</View>
          <View style={{ height: '100%', width: scale(50), justifyContent: 'center' }}>{Additional && <Additional {...{ C, font }} />}</View>
        </View>
      ))}
    </Element>
  );
};

const noPossible = {
  ProductionOrder: false,
  Operation: false,
  Part: false,
};

let metaDataIDs = {};

export const trigerSelectFN = (item, id, items, updateItems, updateImageMetadata, setImages, images) => {
  const newValue = !item.selected;

  let prevFavorite;
  const newItems = items.map((i) => {
    if (i.id === item.id) {
      prevFavorite = i.favorite;
      return { ...i, selected: newValue, favorite: newValue ? i.favorite : false };
    }
    return i;
  });

  let noPossibleValue = true;

  newItems.forEach((i) => {
    if (noPossible[i.key] !== i.selected) {
      noPossibleValue = false;
    }
  });

  if (noPossibleValue) {
    return Alert({ title: i18n.t('Only Operation selected'), text: i18n.t('You have to select either the part or the order tag besides.') });
  }

  updateItems(newItems);

  if (setImages && images) {
    const newImages = images.map((i) => {
      if (i.id === id) {
        return {
          ...i,
          tags: newItems,
        };
      }
      return i;
    });
    setImages(newImages);
  }

  if (metaDataIDs[id]) {
    clearTimeout(metaDataIDs[id]);
  }

  metaDataIDs[id] = setTimeout(async () => {
    const currentImage = images?.find((img) => img.id === id);
    const backendData = {
      documentId: id,
      documentLinks: newItems.map((i) => ({
        scopeId: i.scopeId,
        scopeReferenceId: i.scopeReferenceId,
        isFavorite: i.favorite,
        isActivated: i.selected,
        isFavoriteChanged: item.id === i.id ? prevFavorite !== i.favorite : false,
      })),
      documentTags: currentImage?.documentType ? [currentImage.documentType] : null,
    };

    updateImageMetadata(backendData);
  }, 2000);
};

export const trigerFavoriteFN = (props, ignoreCheckingOther, scale = initialScale) => {
  const { item, id, tags, updateTags, updateImageMetadata, setImages, images } = props;
  const data = {
    documentId: id,
    key: item.key,
    value: item.value,
    favorite: !item.favorite,
  };

  if (!data.favorite) {
    const text = i18n.t('Please tag another image as favorite to automatically untag this image as favorite.');
    return Alert({
      title: text,
      position: 'top',
      type: 'danger',
      style: {
        marginTop: isPhone ? 0 : scale(isWeb ? 120 : 80),
      },
    });
  }

  const isOtherFavorite = images?.find((i) => i?.tags?.find((t) => t.favorite && t.key === item.key));
  if (isOtherFavorite && !ignoreCheckingOther) {
    Popup({
      asAlert: true,
      paddingHorizontal: scale(30),
      Component: (p) => (
        <SelectFavorite
          {...{
            ...p,
            current: isOtherFavorite,
            selected: images.find((i) => i.id === id),
            selectedKey: item.key,
            submitChange: () => trigerFavoriteFN(props, true),
          }}
        />
      ),
    });
    return;
  }

  const newItems = tags.map((i) => {
    if (i.id === item.id) {
      const newValue = !i.favorite;
      const result = {
        favorite: newValue,
      };
      if (newValue) {
        result.selected = true;
      }
      return { ...i, ...result };
    }
    return i;
  });
  updateTags(newItems);

  if (setImages && images) {
    const newImages = images.map((i) => {
      if (i.id === id) {
        return {
          ...i,
          tags: newItems,
        };
      }
      return i;
    });
    setImages(newImages);
  }

  if (metaDataIDs[id]) {
    clearTimeout(metaDataIDs[id]);
  }

  metaDataIDs[id] = setTimeout(async () => {
    const currentImageFav = images?.find((img) => img.id === id);
    const backendData = {
      documentId: id,
      documentLinks: newItems.map((i) => ({
        scopeId: i.scopeId,
        scopeReferenceId: i.scopeReferenceId,
        isFavorite: i.favorite,
        isActivated: i.selected,
        isFavoriteChanged: i.id === item.id,
      })),
      documentTags: currentImageFav?.documentType ? [currentImageFav.documentType] : null,
    };

    updateImageMetadata(backendData);
  }, 2000);
};

export const Pages = ({
  C,
  changePDFImage,
  image = {},
  showActions,
  width: _width = width,
  height: _height = height,
  scale = initialScale,
  font = initialFont,
}) => {
  const Arrow = ({ type }) => (
    <TouchableElement
      onPress={() => {
        if (type && image?.pageCount === image?.page) {
          return;
        }
        if (!type && image?.page === 1) {
          return;
        }
        changePDFImage(image?.id, type);
      }}
    >
      {({ boxStyle, pressHoverColor }) => (
        <View style={{ width: '100%', height: '100%', justifyContent: 'center', alignContent: 'center', alignItems: 'center' }}>
          <View
            style={[
              boxStyle,
              {
                width: scale(36),
                height: scale(36),
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: C(pressHoverColor('Button/Light/On Picture')),
                borderRadius: scale(36) / 2,
                shadowColor: C('Shadow / Light / Narrow'),
                shadowOffset: {
                  width: 0,
                  height: 1,
                },
                shadowOpacity: 0.15,
                shadowRadius: 2.22,
                elevation: 3,
              },
            ]}
          >
            <Svg icon="arrow" color="Label/Light/AlwaysLight Primary" width={scale(20)} height={scale(isWeb ? 15 : 20)} />
          </View>
        </View>
      )}
    </TouchableElement>
  );
  return showActions && image && image?.pageCount > 1 ? (
    <Element
      scale={scale}
      {...{
        config: {
          width: 184,
          height: 36,
          zIndex: 10000,
          horizontalCenter: 0,
          parentWidth: _width,
          position: {
            bottom: (isPhone ? 141 : 77) + notchBottom,
          },
        },
      }}
    >
      <Element
        scale={scale}
        {...{
          config: {
            position: {
              top: 5,
            },
            width: 184,
            height: 26,
            backgroundColor: C('Overlay/Light/Dim strong'),
            justifyContent: 'center',
            alignItems: 'center',
            alignContent: 'center',
            borderRadius: scale(13),
          },
        }}
      >
        <Text style={[font['Caption 2'], { color: C('Label/Light/AlwaysLight Primary') }]}>
          {i18n.t('Page')} {image?.page} {i18n.t('of')} {image?.pageCount}
        </Text>
      </Element>
      <Element
        scale={scale}
        {...{
          config: {
            position: {
              top: -9,
              left: -9,
            },
            width: 54,
            height: 54,
          },
        }}
      >
        <Arrow type={false} />
      </Element>
      <Element
        scale={scale}
        {...{
          config: {
            position: {
              top: -9,
              right: -9,
            },
            transform: [{ rotate: '180deg' }],
            width: 54,
            height: 54,
          },
        }}
      >
        <Arrow type={true} />
      </Element>
    </Element>
  ) : (
    <View />
  );
};

let rotateIDs = {};

export const rotateImageFN = async ({ images, selectedIndex, setImages, onePage, saveImageRotate }) => {
  const img = images[selectedIndex];
  if (!img) {
    return;
  }
  const { customRotate, page, pageCount, id, isPDF, cropped } = img;

  let angle = (customRotate[page] || 0) + 90;
  const list = {};
  const newImages = images.map((i) => {
    if (i.id === id) {
      if (onePage) {
        if (i.cropped) {
          const initialAngle = i.cropSettings?.find((s) => s.pageNumber === page).rotationAngle;
          return {
            ...i,
            angle: convertAngle(initialAngle + angle),
            width: i.height,
            height: i.width,
            y: i.x,
            x: i.size3x.height - i.y - i.height,
            customRotate: {
              ...i.customRotate,
              [page]: convertAngle(angle),
            },
          };
        } else {
          return {
            ...i,
            customRotate: {
              ...i.customRotate,
              [page]: convertAngle(angle),
            },
          };
        }
      } else {
        Array.from({ length: pageCount }, (_, i) => i + 1).map((i) => {
          list[i] = convertAngle(angle);
        });
        return {
          ...i,
          customRotate: list,
        };
      }
    }
    return i;
  });
  setImages(newImages);

  if (rotateIDs[selectedIndex]) {
    clearTimeout(rotateIDs[selectedIndex]);
  }

  rotateIDs[selectedIndex] = setTimeout(async () => {
    let newAngl = angle;
    if (cropped) {
      const currentAngle = img.cropSettings.find((s) => s.pageNumber === page).rotationAngle;
      newAngl += currentAngle;
    }
    const angl = convertAngle(newAngl);
    const backendData = { documentId: id, rotationAngle: angl, isCroppedVersion: Boolean(cropped), pageNumber: 1 };
    if (isPDF) {
      if (onePage) {
        backendData.pageNumber = page;
      } else {
        backendData.pageNumber = null;
      }
    }
    await saveImageRotate(backendData);
  }, 2000);
};

export const NoData = ({ C, withoutBackButtonForNoData, shouldUseColorWrapper = true, bg = 'Background / Dark / System' }) => {
  return (
    <View
      style={{
        backgroundColor: !shouldUseColorWrapper ? bg : C(bg),
        flex: 1,
        justifyContent: 'center',
        alignContent: 'center',
        alignItems: 'center',
      }}
    >
      {!withoutBackButtonForNoData && (
        <View style={{ width, height: '100%', position: 'absolute' }}>
          <Element
            scale={scale}
            {...{
              config: {
                position: {
                  left: 4,
                  top: notchTop,
                },
                alignContent: 'center',
                alignItems: 'center',
                justifyContent: 'center',
              },
            }}
          >
            <BackButton />
          </Element>
        </View>
      )}
      <Text style={{ color: C('Label / Light / Primary') }}>{i18n.t('No data')}</Text>
    </View>
  );
};

let showActionID = null;
export const trigerShowActionsFN = ({ value, setShowActions, noDelay }) => {
  if (noDelay) {
    setShowActions(value);
    showActionID = null;
    return;
  }

  if (showActionID) {
    clearTimeout(showActionID);
    showActionID = null;
    return;
  }
  showActionID = setTimeout(() => {
    setShowActions(value);
    showActionID = null;
  }, 400);
};

export const noImageWrapper = ({ fn, images, selectedIndex }) => {
  const img = images[selectedIndex];
  if (!img || !img.id) {
    return;
  }
  fn();
};

export const generateThumbnailImages = (imagesList) => {
  if (!imagesList || !imagesList.length) {
    return { groupedImages: {}, groupedImagesKeys: [], images: [] };
  }

  const images = [...imagesList];

  const groupedImages = {};

  const indexID = {};

  images.forEach((i, index) => {
    let amountOfFavorite = 0;
    let lastTag = i?.tags?.[0] || {};
    i?.tags?.forEach((t) => {
      if (lastTag.scopeId < t.scopeId) {
        lastTag = t;
      }
      if (t.isFavorite) {
        amountOfFavorite += 1;
      }
    });

    const key = `${lastTag.key}-${lastTag.value}`;
    if (!groupedImages[key]) {
      groupedImages[key] = {
        tag: lastTag,
        images: [],
        scopeId: lastTag.scopeId,
        key: key,
      };
    }
    groupedImages[key].images.push(Object.assign({ index, imageTagKey: key, isFavorite: lastTag.isFavorite }, i));
  });

  const groupedImagesKeys = Object.keys(groupedImages).sort(compareOrder);

  images.forEach((i, index) => {
    indexID[i.id] = index;
  });

  Object.values(groupedImages).forEach((g) => {
    g.images = g.images.map((i) => ({ ...i, index: indexID[i.id] })).reverse();
  });

  return {
    groupedImages,
    groupedImagesKeys: groupedImagesKeys.reverse(),
    images: [...images],
  };
};

export const getThumbnailImageSize = (scale = initialScale) => {
  const imagesInLine = isPhone ? 2 : 3;
  let marginRightBase = scale(2);
  const w = isWeb ? Dimensions.get('window').width : width;
  const imageSize = Math.floor((w - marginRightBase * (imagesInLine - 1)) / imagesInLine);
  return { imageSize, imagesInLine, marginRightBase };
};

export const getThumbnailImagePosition = ({ images, selectedIndex, groupedImages, groupedImagesKeys, scale = initialScale }) => {
  if (!images || !images.length) {
    return 0;
  }
  let { imageSize, imagesInLine, marginRightBase } = getThumbnailImageSize(scale);
  imageSize = imageSize + marginRightBase;
  const image = images[selectedIndex];

  let imageGroupedIndex = 0;
  let otherGroupsOffset = 0;
  for (const key of groupedImagesKeys) {
    let found = false;
    groupedImages[key].images.forEach((i, index) => {
      if (i?.id === image?.id) {
        imageGroupedIndex = index;
        found = true;
      }
    });
    if (found) {
      break;
    } else {
      const length = groupedImages[key].images.length;
      const lines = Math.ceil(length / imagesInLine);
      otherGroupsOffset += lines * imageSize;
    }
  }

  const line = Math.ceil((imageGroupedIndex + 1) / imagesInLine);
  const lineOffsets = line * imageSize;
  const baseHeaderOffset = isWeb ? 70 : scale(107);
  const keyPosition = groupedImagesKeys.indexOf(image?.imageTagKey) + 1;
  const headersOffset = keyPosition * baseHeaderOffset;

  const h = isPhone ? height - notchBottom - scale(40) : height;
  const totalOffset = Math.min(h - (lineOffsets + headersOffset + otherGroupsOffset), 0);
  return Math.abs(totalOffset);
};

export const addImageModal = (context) => {
  const possibleKeys = ['Part', 'ProductionOrder', 'Operation', 'Clamping', 'Machine'];

  const tags = [];

  Object.keys(context.filters).forEach((key) => {
    if (possibleKeys.includes(key)) {
      tags.push({
        scopeId: KEYS_TAGS[key],
        scopeReferenceId: context.filters[key].value,
        isFavorite: false,
      });
    }
  });
  ImageUpload({
    saveToMainStore: true,
    tags,
  });
};

export const getImageContext = () => {
  const route = navigationRef?.current?.getCurrentRoute();
  const { p, r, rOp, c, b, pO, pOp, t, m, documentId: rawDocumentId } = route?.params ?? {};
  const documentId = parseRouteParamAsNumber(rawDocumentId);

  let filters = {};
  if (route?.params) {
    if (p) {
      filters.Part = {
        value: `${p}`,
        valueType: 'number',
      };
    }

    if (r) {
      filters.Recipe = {
        value: `${r}`,
        valueType: 'number',
      };
    }

    if (rOp) {
      filters.RecipeOperation = {
        value: `${rOp}`,
        valueType: 'number',
      };
    }

    if (c) {
      filters.Clamping = {
        value: `${c}`,
        valueType: 'number',
      };
    }

    if (b) {
      filters.ProductionBatch = {
        value: `${b}`,
        valueType: 'number',
      };
    }

    if (pO) {
      filters.ProductionOrder = {
        value: `${pO}`,
        valueType: 'number',
      };
    }

    if (pOp) {
      filters.ProductionOperation = {
        value: `${pOp}`,
        valueType: 'number',
      };
    }

    if (t) {
      filters.Tool = {
        value: `${t}`,
        valueType: 'number',
      };
    }

    if (m) {
      filters.Machine = {
        value: `${m}`,
        valueType: 'number',
      };
    }

    if (documentId) {
      filters.DocumentId = {
        value: `${documentId}`,
        valueType: 'string',
      };
    }
  }

  return { filters };
};

export const scalingImage = ({ images, selectedIndex, downloadedImagesGlobal, updateImageBlob }) => {
  const url = images[selectedIndex].urlZoom;
  if (downloadedImagesGlobal[selectedIndex]) {
    return;
  }

  downloadedImagesGlobal[selectedIndex] = true;

  try {
    fetch(url, { ignoreInterceptor: 'true' })
      .then((response) => response.blob())
      .then((blob) => {
        var reader = new FileReader();
        reader.onload = function () {
          updateImageBlob({
            id: images[selectedIndex].id,
            base64: this.result,
          });
        };
        reader.readAsDataURL(blob);
      });
  } catch (e) {
    console.log('Error to fetch image', e);
  }
};

export const selectedIndexChanged = ({ imagesList, selectedIndex, groupedImages, groupedImagesKeys, thumbnailScrollRef }) => {
  setTimeout(() => {
    const offset = getThumbnailImagePosition({ images: imagesList, selectedIndex, groupedImages, groupedImagesKeys });
    if (thumbnailScrollRef && thumbnailScrollRef.current) {
      thumbnailScrollRef.current.scrollTo({ y: offset, animated: false });
    }
  }, 1000);
};

export const IMAGE_HANDLING_HEADER_HEIGHT = 64;
